import { PluginJsonSchema, PluginJsonSchemaProperty } from './plugin-schema-types'

type CoercionSuccess = { success: true; data: Record<string, unknown> }
type CoercionFailure = { success: false; errors: Record<string, string> }
export type CoercionResult = CoercionSuccess | CoercionFailure

/**
 * Resolves the effective type for a JSON Schema property, looking through
 * anyOf/oneOf unions (e.g. nullable fields) to find the primary non-null type.
 */
export function resolveEffectiveType(
  schema: PluginJsonSchemaProperty | undefined,
): string | undefined {
  if (!schema) {
    return undefined
  }
  if (schema.type) {
    return schema.type
  }
  const union = schema.anyOf || schema.oneOf
  if (union) {
    const nonNull = union.find((s) => s.type && s.type !== 'null')
    if (nonNull) {
      return nonNull.type
    }
  }
  return undefined
}

/**
 * Resolves the items schema for an array property, looking through anyOf/oneOf
 * unions when the array is part of a nullable union.
 */
export function resolveItemsSchema(
  schema: PluginJsonSchemaProperty | undefined,
): PluginJsonSchemaProperty | undefined {
  if (!schema) {
    return undefined
  }
  if (schema.items) {
    return schema.items
  }
  const union = schema.anyOf || schema.oneOf
  if (union) {
    const nonNull = union.find((s) => s.type && s.type !== 'null')
    return nonNull?.items
  }
  return undefined
}

/** True when the property's type union admits null (e.g. a nullable number from `z.number().nullable()`). */
export function isNullableType(schema: PluginJsonSchemaProperty | undefined): boolean {
  if (!schema) {
    return false
  }
  const union = schema.anyOf || schema.oneOf
  return !!union?.some((s) => s.type === 'null')
}

function coerceBoolean(value: string): boolean | undefined {
  if (value === 'true') {
    return true
  }
  if (value === 'false') {
    return false
  }
  return undefined
}

function coerceArrayElements(
  elements: string[],
  itemsSchema: PluginJsonSchemaProperty | undefined,
): { values: unknown[] } | { error: string } {
  const itemType = itemsSchema?.type
  const nonEmpty = elements.filter((el) => el !== '')

  if (itemType === 'number' || itemType === 'integer') {
    const numbers = nonEmpty.map(Number)
    if (numbers.some(isNaN)) {
      return { error: 'All values must be numbers' }
    }
    return { values: numbers }
  }

  if (itemType === 'boolean') {
    const booleans = nonEmpty.map(coerceBoolean)
    if (booleans.some((b) => b === undefined)) {
      return { error: 'All values must be true or false' }
    }
    return { values: booleans as boolean[] }
  }

  // string or unknown item type: pass through as-is
  return { values: nonEmpty }
}

type LeafResult = { value: unknown } | { error: string } | { omit: true }

/** Normalizes a field value (a string from a text field, or an untouched typed value) to its display string. */
function toFieldString(value: unknown): string {
  if (value === null || value === undefined) {
    return ''
  }
  if (Array.isArray(value)) {
    return value.join(',')
  }
  return String(value)
}

/** Coerces a single (non-object) field value to its typed form, or signals an error / omission. */
function coerceLeaf(
  value: unknown,
  schema: PluginJsonSchemaProperty | undefined,
  isRequired: boolean,
): LeafResult {
  const type = resolveEffectiveType(schema)
  const str = toFieldString(value)

  // A blank array field means an empty list (e.g. a customer with no instant tickets), not "use the default".
  if (type === 'array') {
    const elements = str.split(',').map((el) => el.trim())
    const result = coerceArrayElements(elements, resolveItemsSchema(schema))
    return 'error' in result ? { error: result.error } : { value: result.values }
  }

  if (str === '') {
    if (isRequired) {
      return { error: 'This field is required' }
    }
    // A blank nullable field is an explicit null (e.g. "keep the full barcode length"); other optional fields are omitted.
    return isNullableType(schema) ? { value: null } : { omit: true }
  }

  if (type === 'number' || type === 'integer') {
    const parsed = Number(str)
    return isNaN(parsed) ? { error: 'Must be a number' } : { value: parsed }
  }
  if (type === 'boolean') {
    const parsed = coerceBoolean(str)
    return parsed === undefined ? { error: 'Must be true or false' } : { value: parsed }
  }
  return { value: str }
}

/**
 * Coerces a (possibly nested) object of field values against the JSON Schema, recursing into object-typed
 * properties. Errors are keyed by dot-path (e.g. "retry.maxAttempts") so the editor can locate them.
 */
function coerceObject(
  values: Record<string, unknown>,
  schema: PluginJsonSchema,
  pathPrefix: string,
): CoercionResult {
  const errors: Record<string, string> = {}
  const data: Record<string, unknown> = {}
  const requiredFields = schema.required || []

  if (!schema.properties) {
    return { success: true, data }
  }

  for (const [key, propertyDef] of Object.entries(schema.properties)) {
    const propertySchema = typeof propertyDef === 'boolean' ? undefined : propertyDef
    const isRequired = requiredFields.includes(key)
    const path = pathPrefix ? `${pathPrefix}.${key}` : key
    const childValue = values[key]

    if (resolveEffectiveType(propertySchema) === 'object' && propertySchema?.properties) {
      const nested = coerceObject(
        (childValue as Record<string, unknown>) ?? {},
        propertySchema,
        path,
      )
      if (nested.success) {
        data[key] = nested.data
      } else {
        Object.assign(errors, nested.errors)
      }
      continue
    }

    const result = coerceLeaf(childValue, propertySchema, isRequired)
    if ('error' in result) {
      errors[path] = result.error
    } else if ('value' in result) {
      data[key] = result.value
    }
  }

  if (Object.keys(errors).length > 0) {
    return { success: false, errors }
  }
  return { success: true, data }
}

/**
 * Takes the values from the config form (text-field strings, or untouched typed values from a loaded config)
 * and the JSON Schema, and returns either coerced typed values or a map of dot-path-keyed field errors.
 */
export function coerceConfigValues(
  values: Record<string, unknown>,
  schema: PluginJsonSchema,
): CoercionResult {
  return coerceObject(values, schema, '')
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

/** Merges `override` onto `base`, recursing into nested plain objects; arrays and scalars in `override` replace. */
function deepMerge(
  base: Record<string, unknown>,
  override: Record<string, unknown>,
): Record<string, unknown> {
  const out: Record<string, unknown> = { ...base }
  for (const [key, value] of Object.entries(override)) {
    out[key] =
      isPlainObject(value) && isPlainObject(out[key])
        ? deepMerge(out[key] as Record<string, unknown>, value)
        : value
  }
  return out
}

/** Builds the default values declared by a schema level, recursing into object properties without their own default. */
function schemaDefaults(properties: PluginJsonSchema['properties']): Record<string, unknown> {
  const defaults: Record<string, unknown> = {}
  for (const [key, propertyDef] of Object.entries(properties ?? {})) {
    const propertySchema = typeof propertyDef === 'boolean' ? undefined : propertyDef
    if (!propertySchema) {
      continue
    }
    if (propertySchema.default !== undefined) {
      defaults[key] = propertySchema.default
    } else if (resolveEffectiveType(propertySchema) === 'object' && propertySchema.properties) {
      defaults[key] = schemaDefaults(propertySchema.properties)
    }
  }
  return defaults
}

/**
 * Pre-fills a stored config with the schema's declared defaults for any field it omits, so the form shows the
 * effective values an operator is editing (and a no-op save preserves them instead of clobbering to empty/null).
 * Stored values — including an explicit null — always win over a default.
 */
export function applySchemaDefaults(
  values: Record<string, unknown>,
  schema: PluginJsonSchema,
): Record<string, unknown> {
  return deepMerge(schemaDefaults(schema.properties), values)
}

/**
 * Formats a real typed value (from backend) into a display string for a text field.
 */
export function formatValueForDisplay(value: unknown): string {
  if (value === null || value === undefined) {
    return ''
  }

  if (Array.isArray(value)) {
    return value.join(', ')
  }

  return String(value)
}
